import React, { useEffect, useState } from "react";
import axios from "axios";
import { FaHome, FaUserCircle, FaUsers, FaTrash } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import Navbar from "../components/Navbar";

const API_BASE = process.env.REACT_APP_URL;

// Adds JWT token to axios requests
const authConfig = () => ({
  headers: { Authorization: `Bearer ${localStorage.getItem("token") || ""}` }
});

export default function UsersPage() {
  const [users, setUsers] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageInput, setPageInput] = useState("");
  const usersPerPage = 10;
  const navigate = useNavigate();

  const [darkMode, setDarkMode] = useState(false);

  useEffect(() => {
    const syncTheme = () => {
      const savedMode = localStorage.getItem("darkMode");
      setDarkMode(savedMode ? JSON.parse(savedMode) : false);
    };
    syncTheme();
    window.addEventListener("themeChanged", syncTheme);
    return () => window.removeEventListener("themeChanged", syncTheme);
  }, []);

  useEffect(() => {
    axios
      .get(`${API_BASE}/users`, authConfig())
      .then((res) => setUsers(res.data))
      .catch((err) => console.error(err));
  }, []);

  const indexOfLastUser = currentPage * usersPerPage;
  const indexOfFirstUser = indexOfLastUser - usersPerPage;
  const currentUsers = users.slice(indexOfFirstUser, indexOfLastUser);
  const totalPages = Math.ceil(users.length / usersPerPage);

  const confirmDelete = (user) => {
    setUserToDelete(user);
    setShowModal(true);
  };

  const handleDelete = async () => {
    if (!userToDelete) return;
    try {
      await axios.delete(`${API_BASE}/users/${userToDelete.id}`, authConfig());
      setUsers(users.filter((u) => u.id !== userToDelete.id));
      setShowModal(false);
      setUserToDelete(null);
    } catch (err) {
      console.error("Error deleting user:", err);
      alert("Failed to delete user!");
    }
  };

  const toggleUserStatus = async (user) => {
    const newStatus = !user.activeStatus;

    try {
      await axios.put(
        `${API_BASE}/users/status/${user.email}`,
        { status: newStatus },
        authConfig()
      );

      setUsers((prev) =>
        prev.map((u) =>
          u.email === user.email ? { ...u, activeStatus: newStatus } : u
        )
      );
    } catch (err) {
      console.error("Status update error:", err);
      alert("Failed to update user status");
    }
  };

  const renderStatusToggle = (isActive) => (
    <div
      style={{
        ...styles.toggleBase,
        backgroundColor: isActive ? "#4CAF50" : "#ccc",
      }}
    >
      <div
        style={{
          ...styles.toggleCircle,
          transform: isActive ? "translateX(20px)" : "translateX(0)",
        }}
      ></div>
    </div>
  );

  return (
    <div className={darkMode ? "dark-mode" : ""} style={{
      ...styles.page,
      background: darkMode ? "transparent" : "linear-gradient(135deg, #f4f2f8, #eae6f3)"
    }}>
      <Navbar />

      <div style={{ height: "70px" }}></div>

      <div style={styles.container}>
        <div style={styles.header}>
          <button style={styles.backButton} onClick={() => navigate("/admin")}>
            ⮜
          </button>

          <div style={styles.titleContainer}>
            <FaUsers
              size={24}
              style={{ marginRight: "8px", color: "#636CCB" }}
            />
            <h1 style={styles.title}>Users List</h1>
          </div>
          <div style={{ width: styles.backButton.width || 'auto', visibility: 'hidden' }}></div>
        </div>

        <div style={styles.card}>
          {users.length > 0 ? (
            <>
              <div style={{ overflowX: "auto" }}>
                <table style={styles.table}>
                  <thead>
                    <tr style={styles.tableHeader}>
                      <th style={styles.thCenter}>Sl. No</th>
                      <th style={styles.thLeft}>Name</th>
                      <th style={styles.thLeft}>Email</th>
                      <th style={styles.thLeft}>Status</th>
                      <th style={styles.thLeft}>Created</th>
                      <th style={styles.thCenter}></th>
                    </tr>
                  </thead>

                  <tbody>
                    {currentUsers.map((user, index) => (
                      <tr
                        key={user.id}
                        style={{
                          backgroundColor:
                            index % 2 === 0 ? "var(--zebra-stripe)" : "transparent",
                          borderBottom: "1px solid var(--border-light)",
                        }}
                      >
                        <td style={styles.cellCenter}>
                          {(currentPage - 1) * usersPerPage + index + 1}
                        </td>

                        <td style={styles.cellLeftBold}>{user.username}</td>

                        <td style={styles.cellLeftTruncate}>{user.email}</td>

                        <td style={styles.cellStatus}
                          onClick={() => toggleUserStatus(user)} >
                          {renderStatusToggle(user.activeStatus)}
                        </td>

                        <td style={styles.cellLeftDate}>
                          {new Date(user.createdAt).toLocaleString()}
                        </td>

                        <td style={styles.cellCenter}>
                          <FaTrash
                            title="Delete user"
                            style={styles.deleteIcon}
                            onClick={() => confirmDelete(user)}
                          />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div style={styles.pagination}>
                {/* First Page Button */}
                <button
                  style={{
                    ...styles.navButton,
                    opacity: currentPage === 1 ? 0.4 : 1,
                    cursor: currentPage === 1 ? "not-allowed" : "pointer",
                  }}
                  disabled={currentPage === 1}
                  onClick={() => setCurrentPage(1)}
                  title="First Page"
                >
                  «
                </button>

                {/* Previous Page Button */}
                <button
                  style={{
                    ...styles.navButton,
                    opacity: currentPage === 1 ? 0.4 : 1,
                    cursor: currentPage === 1 ? "not-allowed" : "pointer",
                  }}
                  disabled={currentPage === 1}
                  onClick={() => setCurrentPage(currentPage - 1)}
                  title="Previous Page"
                >
                  ‹
                </button>

                {/* Page Input */}
                <div style={styles.pageInputContainer}>
                  <span style={styles.pageLabel}>Page</span>
                  <input
                    type="number"
                    value={pageInput || currentPage}
                    onChange={(e) => setPageInput(e.target.value)}
                    onBlur={() => {
                      const num = parseInt(pageInput, 10);
                      if (num >= 1 && num <= totalPages) {
                        setCurrentPage(num);
                      }
                      setPageInput("");
                    }}
                    onKeyPress={(e) => {
                      if (e.key === "Enter") {
                        const num = parseInt(pageInput, 10);
                        if (num >= 1 && num <= totalPages) {
                          setCurrentPage(num);
                        }
                        setPageInput("");
                      }
                    }}
                    style={styles.pageInput}
                    min="1"
                    max={totalPages}
                  />
                  <span style={styles.pageLabel}>of {totalPages}</span>
                </div>

                {/* Next Page Button */}
                <button
                  style={{
                    ...styles.navButton,
                    opacity: currentPage === totalPages ? 0.4 : 1,
                    cursor: currentPage === totalPages ? "not-allowed" : "pointer",
                  }}
                  disabled={currentPage === totalPages}
                  onClick={() => setCurrentPage(currentPage + 1)}
                  title="Next Page"
                >
                  ›
                </button>

                {/* Last Page Button */}
                <button
                  style={{
                    ...styles.navButton,
                    opacity: currentPage === totalPages ? 0.4 : 1,
                    cursor: currentPage === totalPages ? "not-allowed" : "pointer",
                  }}
                  disabled={currentPage === totalPages}
                  onClick={() => setCurrentPage(totalPages)}
                  title="Last Page"
                >
                  »
                </button>
              </div>
            </>
          ) : (
            <p style={styles.noData}>
              No users found <FaUsers style={{ color: "#636CCB" }} />
            </p>
          )}
        </div>
      </div>

      <footer style={styles.footer}>
        Navriti Technologies © All rights reserved
      </footer>

      {showModal && (
        <div style={styles.modalOverlay}>
          <div style={{ ...styles.modal, backgroundColor: "var(--card-bg)", color: "var(--text-main)" }}>
            <h3 style={{ ...styles.modalTitle, color: "var(--text-main)" }}>Delete User?</h3>
            <p style={{ ...styles.modalText, color: "var(--text-sub)" }}>
              Are you sure you want to delete <b>{userToDelete?.username}</b>?
            </p>

            <div style={styles.modalButtons}>
              <button
                style={styles.cancelBtn}
                onClick={() => setShowModal(false)}
              >
                Cancel
              </button>
              <button style={styles.deleteBtn} onClick={handleDelete}>
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const styles = {
  page: {
    minHeight: "100vh",
    background: "linear-gradient(135deg, #f4f2f8, #eae6f3)",
    fontFamily: "Poppins, sans-serif",
    paddingBottom: "80px",
  },

  footer: {
    width: "100%",
    backgroundColor: "#636CCB",
    color: "white",
    textAlign: "center",
    height: "40px",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    fontWeight: "600",
    fontSize: "1em",
    position: "fixed",
    bottom: 0,
    left: 0,
    zIndex: 2000,
  },


  backButton: {
    backgroundColor: "#636CCB",
    color: "white",
    border: "none",
    borderRadius: "6px",
    padding: "8px 16px",
    cursor: "pointer",
    fontWeight: "500",
    fontSize: "0.9em",
    boxShadow: "0 4px 8px rgba(0,0,0,0.1)",
    width: "60px",
  },

  container: { padding: "10px 30px 40px" },

  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: "15px",
    padding: "20px 0 0",
  },

  titleContainer: {
    display: "flex",
    alignItems: "center",
  },

  title: {
    fontSize: "1.5em",
    fontWeight: "700",
    color: "var(--text-main)",
    margin: 0,
  },

  card: {
    background: "var(--card-bg)",
    borderRadius: "12px",
    padding: "15px",
    boxShadow: "0 4px 15px rgba(0,0,0,0.07)",
    transition: "background 0.3s, color 0.3s",
  },

  table: {
    width: "100%",
    borderCollapse: "collapse",
    fontSize: "0.9em",
  },

  tableHeader: { backgroundColor: "#636CCB", color: "white" },

  thCenter: { textAlign: "center", padding: "10px", fontWeight: "600" },
  thLeft: { textAlign: "left", padding: "10px", fontWeight: "600" },

  cellCenter: { textAlign: "center", padding: "8px", color: "var(--text-main)" },
  cellLeftBold: { padding: "8px 10px", fontWeight: "600", color: "var(--text-main)" },
  cellLeftDate: { padding: "8px 10px", fontSize: "0.85em", color: "var(--text-sub)" },

  cellLeftTruncate: {
    padding: "8px 10px",
    maxWidth: "200px",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    color: "var(--text-main)",
  },

  cellStatus: {
    width: "120px",
    textAlign: "center",
    padding: "8px",
  },

  deleteIcon: {
    color: "#c0392b",
    fontSize: "1.1em",
    cursor: "pointer",
  },

  pagination: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    gap: "10px",
    marginTop: "15px",
  },

  navButton: {
    border: "none",
    padding: "10px 16px",
    backgroundColor: "var(--text-main)",
    color: "var(--card-bg)",
    borderRadius: "20px",
    cursor: "pointer",
    fontSize: "1.2em",
    fontWeight: "600",
    minWidth: "45px",
    transition: "opacity 0.2s",
  },

  pageInputContainer: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    backgroundColor: "var(--card-bg)",
    border: "2px solid var(--border-light)",
    padding: "8px 16px",
    borderRadius: "20px",
    boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
  },

  pageLabel: {
    fontSize: "1em",
    fontWeight: "600",
    color: "var(--text-main)",
  },

  pageDisplay: {
    fontSize: "1em",
    fontWeight: "600",
    color: "#3b82f6",
  },

  pageInput: {
    width: "50px",
    padding: "4px 8px",
    border: "2px solid #3b82f6",
    borderRadius: "6px",
    textAlign: "center",
    fontSize: "1em",
    fontWeight: "600",
    outline: "none",
    backgroundColor: "var(--card-bg)",
    color: "var(--text-main)",
  },

  toggleBase: {
    width: "38px",
    height: "20px",
    borderRadius: "10px",
    position: "relative",
  },
  toggleCircle: {
    width: "18px",
    height: "18px",
    background: "white",
    borderRadius: "50%",
    position: "absolute",
  },

  modalOverlay: {
    position: "fixed",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: "rgba(0,0,0,0.4)",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
  },

  modal: {
    background: "white",
    padding: "25px 30px",
    borderRadius: "10px",
    width: "350px",
    textAlign: "center",
  },

  modalTitle: { fontWeight: "700", marginBottom: "10px" },
  modalText: { marginBottom: "20px" },

  modalButtons: { display: "flex", justifyContent: "space-around" },

  cancelBtn: { background: "#ccc", padding: "8px 15px", borderRadius: "5px" },
  deleteBtn: {
    background: "#c0392b",
    color: "white",
    padding: "8px 15px",
    borderRadius: "5px",
  },
};